// Singleton dispatcher for the "pick which email account to send from" modal.
// Used by chat tool handlers (send_prospect_email, reply_email...) so that
// when the coach has multiple Gmail/Outlook accounts connected, Thiia opens
// a modal asking which one to use BEFORE actually sending.

export interface SenderPickAccount {
  email: string;
  provider: "gmail" | "outlook" | "resend";
  isDefault?: boolean;
}

/** High-level email action being performed — used for the modal label/icon. */
export type SenderPickAction = "send" | "reply" | "schedule" | "sequence";

export interface SenderPickRequest {
  id: number;
  /** Short context shown in the modal header (e.g. "Envoi à jean@x.com"). */
  context?: string;
  /** Subject of the email about to be sent (optional, shown for clarity). */
  subject?: string;
  /** Type of action so the modal can show the right icon + label. */
  action?: SenderPickAction;
  /** Primary recipient (email or display name) shown prominently. */
  recipient?: string;
  /** For scheduled sends, the human-readable date/time. */
  scheduledAt?: string;
  /** Coach id used to namespace the "remembered last sender" preference. */
  coachId?: string;
  accounts: SenderPickAccount[];
  resolve: (picked: SenderPickAccount | null) => void;
}

type Listener = (req: SenderPickRequest | null) => void;

let listener: Listener | null = null;
let nextId = 1;
let current: SenderPickRequest | null = null;

// ---------------------------------------------------------------------------
// "Remember last sender" — persists the most recently chosen account per coach
// in localStorage so we don't reopen the picker for every email when the coach
// has already made a deliberate choice.
// ---------------------------------------------------------------------------
const REMEMBER_KEY_PREFIX = "thiia.lastSender:";

function rememberKey(coachId?: string): string | null {
  if (!coachId) return null;
  return `${REMEMBER_KEY_PREFIX}${coachId}`;
}

export function getRememberedSender(
  coachId?: string,
): { provider: SenderPickAccount["provider"]; email: string } | null {
  const key = rememberKey(coachId);
  if (!key || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { provider: string; email: string };
    if (!parsed?.email || !parsed?.provider) return null;
    return {
      provider: parsed.provider as SenderPickAccount["provider"],
      email: parsed.email,
    };
  } catch {
    return null;
  }
}

export function setRememberedSender(
  coachId: string | undefined,
  account: SenderPickAccount,
): void {
  const key = rememberKey(coachId);
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({ provider: account.provider, email: account.email }),
    );
  } catch {
    /* quota / private mode — ignore */
  }
}

export function clearRememberedSender(coachId?: string): void {
  const key = rememberKey(coachId);
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function subscribeSenderPick(fn: Listener): () => void {
  listener = fn;
  if (current) fn(current);
  return () => {
    if (listener === fn) listener = null;
  };
}

/**
 * Opens the modal and resolves with the chosen account, or `null` if the user
 * cancels. If only one account is provided, resolves immediately with it.
 *
 * If a previously remembered account (per coach) is still present in
 * `payload.accounts`, the modal is **skipped** and the remembered account is
 * returned directly. The user can clear that preference from the modal.
 */
export function requestSenderPick(
  payload: Omit<SenderPickRequest, "id" | "resolve">,
): Promise<SenderPickAccount | null> {
  // Auto-resolve when there's nothing to choose between.
  if (!payload.accounts || payload.accounts.length <= 1) {
    return Promise.resolve(payload.accounts?.[0] ?? null);
  }

  // If we have a remembered choice that's still valid, skip the modal entirely.
  const remembered = getRememberedSender(payload.coachId);
  if (remembered) {
    const match = payload.accounts.find(
      (a) => a.provider === remembered.provider && a.email === remembered.email,
    );
    if (match) {
      return Promise.resolve(match);
    }
    // Remembered account no longer connected — clean up.
    clearRememberedSender(payload.coachId);
  }

  if (current) {
    current.resolve(null);
    current = null;
  }
  return new Promise<SenderPickAccount | null>((resolve) => {
    current = {
      ...payload,
      id: nextId++,
      resolve: (v) => {
        current = null;
        listener?.(null);
        // Persist the choice (only when a real account was picked, not on cancel)
        if (v) setRememberedSender(payload.coachId, v);
        resolve(v);
      },
    };
    if (listener) {
      listener(current);
    } else {
      // No host mounted — fail-safe: pick the default (or first) account.
      const fallback = payload.accounts.find((a) => a.isDefault) ?? payload.accounts[0] ?? null;
      console.warn("[senderPick] No host mounted, falling back to default account.");
      const c = current;
      current = null;
      resolve(fallback);
      void c;
    }
  });
}
